"use client";

import { useLingui } from "@lingui/react/macro";
import { CheckIcon } from "@offstreetparking/design-system/icons/CheckIcon";
import { CloseIcon } from "@offstreetparking/design-system/icons/CloseIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { useWatch } from "react-hook-form";
import { PASSWORD_RULES } from "./password-rules";

type PasswordRequirementsProps = {
	name: string;
};

export const PasswordRequirements = ({ name }: PasswordRequirementsProps) => {
	const { t, i18n } = useLingui();
	const password: string = useWatch({ name }) ?? "";

	return (
		<ul
			aria-label={t({ message: "Password requirements", comment: "Label for the password rules checklist" })}
			aria-live="polite"
			className="space-y-2"
		>
			{PASSWORD_RULES.map((rule) => {
				const isMet = rule.isMet(password);
				const StatusIcon = isMet ? CheckIcon : CloseIcon;
				return (
					<li key={rule.id} className="flex items-center gap-2">
						<StatusIcon
							aria-hidden="true"
							className={classNames("size-5 shrink-0", isMet ? "text-success" : "text-neutral-faded")}
						/>
						<Text As="span" size="sm" className={isMet ? "text-success" : "text-neutral-faded"}>
							{i18n._(rule.label)}
						</Text>
						<span className="sr-only">
							{isMet
								? t({
										message: "Met",
										comment: "Screen reader status for a satisfied password requirement",
									})
								: t({
										message: "Not met",
										comment: "Screen reader status for an unsatisfied password requirement",
									})}
						</span>
					</li>
				);
			})}
		</ul>
	);
};
